import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCompleteTask,
  newIdempotencyKey,
  familyKeys,
  type DayTasksResponse,
  type MissionsResponse,
  type OccurrenceResponse,
  type ChildBalanceResponse,
} from '@kidshome/api-contract';
import { invalidateHeroSurfaces } from '@/entities/hero';

/**
 * `complete-task` — the optimistic core of the kid day screen. On click we:
 *   1. cancel in-flight day/balance queries, snapshot them,
 *   2. flip the occurrence to its next state in the day cache (instant ✓),
 *   3. bump the coin balance cache by the chore's coin value **together** with the list,
 *   4. carry an `Idempotency-Key` so an offline replay never double-applies,
 *   5. on error surgically undo ONLY this occurrence's own row (never the whole day/balance snapshot,
 *      which would clobber a sibling completion still in flight), and on settle invalidate so the
 *      server (authoritative) reconciles the real state + balance.
 *
 * The next state mirrors the backend FSM: a chore needing approval goes to `pending_approval`
 * (no coins yet); otherwise it auto-approves to `approved` (coins land now).
 */
export interface CompleteTaskContext {
  readonly date: string;
  readonly assigneeId: string;
  /** The child whose coin balance to bump (defaults to the assignee). */
  readonly childId?: string;
}

interface Snapshot {
  day: DayTasksResponse | undefined;
  missions: MissionsResponse | undefined;
  balance: ChildBalanceResponse | undefined;
  occurrence: OccurrenceResponse | undefined;
  dayKey: readonly unknown[];
  missionsKey: readonly unknown[];
  balanceKey: readonly unknown[];
  occurrenceKey: readonly unknown[];
  /** The assignee THIS mutation was issued for — pinned so `onSettled` reconciles the right week agenda
   * even if the caller has since re-rendered with a different date/assignee (e.g. the kid navigated
   * to another day/week while the request was still in flight). */
  assigneeId: string;
}

function nextStatusFor(occ: OccurrenceResponse): OccurrenceResponse['status'] {
  return occ.requiresApproval ? 'pending_approval' : 'approved';
}

function applyCompletion(day: DayTasksResponse, occurrenceId: string): DayTasksResponse {
  return {
    ...day,
    groups: day.groups.map((g) => ({
      ...g,
      occurrences: g.occurrences.map((o) =>
        o.id === occurrenceId ? { ...o, status: nextStatusFor(o) } : o,
      ),
    })),
  };
}

/** The Missions-lane analogue of {@link applyCompletion} — a mission is an open-ended one-time chore
 * completed from Lane B, so the same optimistic flip keeps that card instant too (ADR-0141). */
function applyCompletionMissions(
  missions: MissionsResponse,
  occurrenceId: string,
): MissionsResponse {
  return {
    ...missions,
    missions: missions.missions.map((o) =>
      o.id === occurrenceId ? { ...o, status: nextStatusFor(o) } : o,
    ),
  };
}

function revertOccurrenceInMissions(
  missions: MissionsResponse,
  occurrenceId: string,
  status: OccurrenceResponse['status'],
): MissionsResponse {
  return {
    ...missions,
    missions: missions.missions.map((o) => (o.id === occurrenceId ? { ...o, status } : o)),
  };
}

/** The status THIS occurrence held before `onMutate` optimistically advanced it — read from the
 * captured pre-mutation snapshot (day-list first, single-occurrence fallback for the cold-day case).
 * Used to surgically undo only this row on error. `undefined` if the occurrence wasn't in either. */
function originalStatusOf(
  day: DayTasksResponse | undefined,
  missions: MissionsResponse | undefined,
  occurrence: OccurrenceResponse | undefined,
  occurrenceId: string,
): OccurrenceResponse['status'] | undefined {
  if (day) {
    for (const g of day.groups) {
      for (const o of g.occurrences) {
        if (o.id === occurrenceId) return o.status;
      }
    }
  }
  if (missions) {
    for (const o of missions.missions) {
      if (o.id === occurrenceId) return o.status;
    }
  }
  if (occurrence && occurrence.id === occurrenceId) return occurrence.status;
  return undefined;
}

/** Reset ONE occurrence row back to `status` inside the *current* day cache, leaving every other row
 * untouched — including a sibling completion that optimistically advanced the same shared day cache
 * while this request was still in flight. */
function revertOccurrenceInDay(
  day: DayTasksResponse,
  occurrenceId: string,
  status: OccurrenceResponse['status'],
): DayTasksResponse {
  return {
    ...day,
    groups: day.groups.map((g) => ({
      ...g,
      occurrences: g.occurrences.map((o) => (o.id === occurrenceId ? { ...o, status } : o)),
    })),
  };
}

/** Coins only land immediately for an auto-approved chore; approval-gated ones credit later. Also
 * guards against an overlapping second `complete()` on the same occurrence (e.g. a rapid double-tap
 * before the button's `disabled` state re-renders): once a prior optimistic update (or the server) has
 * already moved this occurrence PAST `planned`/`missed`/`rejected`, it is no longer eligible to credit
 * coins again — only the FIRST transition into `approved` should. */
function coinDeltaForFields(
  o: Pick<OccurrenceResponse, 'requiresApproval' | 'status' | 'coinValue'>,
): number {
  if (o.requiresApproval) return 0;
  return o.status === 'approved' || o.status === 'pending_approval' ? 0 : o.coinValue;
}

function coinDeltaFor(
  day: DayTasksResponse | undefined,
  missions: MissionsResponse | undefined,
  occurrence: OccurrenceResponse | undefined,
  occurrenceId: string,
): number {
  if (day) {
    for (const g of day.groups) {
      for (const o of g.occurrences) {
        if (o.id !== occurrenceId) continue;
        return coinDeltaForFields(o);
      }
    }
  }
  if (missions) {
    for (const o of missions.missions) {
      if (o.id !== occurrenceId) continue;
      return coinDeltaForFields(o);
    }
  }
  // The day-list cache can be cold even when the single-occurrence cache is already warm — e.g. a kid
  // who deep-links straight into `/chore/:id` (a notification, a home-screen shortcut) without visiting
  // the day/today view first this session. `occurrence` carries the SAME `coinValue`/`requiresApproval`/
  // `status` fields, so fall back to it instead of silently reporting no delta and skipping a real
  // auto-approved chore's optimistic balance bump.
  if (occurrence && occurrence.id === occurrenceId) {
    return coinDeltaForFields(occurrence);
  }
  return 0;
}

/** The minimal card identity a completion needs: its optimistic id + the natural key it wires (ADR-0142). */
export interface CompletableCard {
  readonly id: string;
  readonly templateId: string;
  readonly scheduledLocalDate: string;
  readonly assigneeId: string;
}

export function useCompleteTaskAction(ctx: CompleteTaskContext): {
  /** Complete a chore card, addressed by its `(templateId, localDate)`; pass `photoProofKey` for a
   * photo-proof chore that already has an uploaded proof (records it on completion). */
  complete: (card: CompletableCard, photoProofKey?: string) => void;
  /** True while ANY completion is in flight (kept for callers with a single control). */
  isPending: boolean;
  /** True while THIS occurrence's completion is in flight (per-row pending, M6). */
  isOccurrencePending: (occurrenceId: string) => boolean;
  /** The last completion failure (so the caller can surface it), or null. Cleared on the next try. */
  error: unknown;
} {
  const qc = useQueryClient();
  const childId = ctx.childId ?? ctx.assigneeId;
  const dayKey = familyKeys.day(ctx.assigneeId, ctx.date);
  const missionsKey = familyKeys.missions(ctx.assigneeId);
  const balanceKey = familyKeys.balance(childId);

  // Track in-flight occurrence ids so each row's pending state is independent — a kid can check off
  // several chores at once without one in-flight write blocking the others. Each `mutate` keeps its
  // own snapshot/rollback context, so concurrent completions stay correct per row.
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set());

  const mutation = useCompleteTask<Snapshot>({
    onMutate: async ({ optimisticId: occurrenceId }) => {
      setPendingIds((prev) => new Set(prev).add(occurrenceId));
      const occurrenceKey = familyKeys.occurrence(occurrenceId);
      await Promise.all([
        qc.cancelQueries({ queryKey: dayKey }),
        qc.cancelQueries({ queryKey: missionsKey }),
        qc.cancelQueries({ queryKey: balanceKey }),
        qc.cancelQueries({ queryKey: occurrenceKey }),
      ]);
      const day = qc.getQueryData<DayTasksResponse>(dayKey);
      const missions = qc.getQueryData<MissionsResponse>(missionsKey);
      const balance = qc.getQueryData<ChildBalanceResponse>(balanceKey);
      const occurrence = qc.getQueryData<OccurrenceResponse>(occurrenceKey);
      const delta = coinDeltaFor(day, missions, occurrence, occurrenceId);

      if (day) {
        qc.setQueryData<DayTasksResponse>(dayKey, applyCompletion(day, occurrenceId));
      }
      if (missions) {
        qc.setQueryData<MissionsResponse>(
          missionsKey,
          applyCompletionMissions(missions, occurrenceId),
        );
      }
      if (balance && delta > 0) {
        qc.setQueryData<ChildBalanceResponse>(balanceKey, {
          ...balance,
          coins: balance.coins + delta,
        });
      }
      // Flip the single-occurrence cache too so the chore-detail page reflects the new state INSTANTLY
      // — the complete/photo control disappears immediately, closing the double-fire window that
      // otherwise lets a second tap `complete` an already-`pending_approval` occurrence (409).
      if (occurrence) {
        qc.setQueryData<OccurrenceResponse>(occurrenceKey, {
          ...occurrence,
          status: nextStatusFor(occurrence),
        });
      }
      return {
        day,
        missions,
        balance,
        occurrence,
        dayKey,
        missionsKey,
        balanceKey,
        occurrenceKey,
        assigneeId: ctx.assigneeId,
      };
    },
    onError: (_err, { optimisticId: occurrenceId }, context) => {
      if (!context) return;
      // Surgically undo ONLY this occurrence's optimistic flip inside the LATEST day cache. Restoring
      // the whole captured `context.day` here would clobber a SIBLING completion that optimistically
      // advanced the same shared day cache while this request was in flight (a kid tapping two chores in
      // quick succession) — the sibling's row would flash back to incomplete until `onSettled` refetches.
      // Every other row, including that in-flight sibling, is left exactly as it stands.
      const originalStatus = originalStatusOf(
        context.day,
        context.missions,
        context.occurrence,
        occurrenceId,
      );
      const currentDay = qc.getQueryData<DayTasksResponse>(context.dayKey);
      if (currentDay && originalStatus) {
        qc.setQueryData<DayTasksResponse>(
          context.dayKey,
          revertOccurrenceInDay(currentDay, occurrenceId, originalStatus),
        );
      }
      // Mirror the surgical per-row revert on the Missions cache (a mission optimistically advanced from
      // Lane B), leaving every other mission row untouched.
      const currentMissions = qc.getQueryData<MissionsResponse>(context.missionsKey);
      if (currentMissions && originalStatus) {
        qc.setQueryData<MissionsResponse>(
          context.missionsKey,
          revertOccurrenceInMissions(currentMissions, occurrenceId, originalStatus),
        );
      }
      // The single-occurrence cache is keyed to THIS occurrence alone (never shared with a sibling), so
      // restoring its captured snapshot is both safe and precise.
      qc.setQueryData(context.occurrenceKey, context.occurrence);
      // Deliberately DON'T rewrite the shared balance cache: restoring `context.balance` would drop a
      // sibling's still-in-flight coin bump (the same clobber, on the balance). `onSettled` invalidates +
      // refetches the authoritative balance moments later, so a brief over-count of this failed chore's
      // own coins self-heals — far preferable to reverting a sibling's legitimate optimistic credit.
    },
    onSettled: (_data, _err, { optimisticId: occurrenceId }, context) => {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(occurrenceId);
        return next;
      });
      // Reconcile the day/balance/week THIS mutation was actually issued for — read from the pinned
      // `context` (captured by `onMutate` at mutate-time), NOT the outer-scope `dayKey`/`balanceKey`/
      // `ctx.assigneeId` closures, which TanStack Query silently rebinds to the LATEST render's props
      // on every render (incl. while this mutation is still in flight). Without this, navigating to a
      // different day/week before the request settles reconciles the WRONG day's cache.
      const dayKeyToInvalidate = context?.dayKey ?? dayKey;
      const missionsKeyToInvalidate = context?.missionsKey ?? missionsKey;
      const balanceKeyToInvalidate = context?.balanceKey ?? balanceKey;
      const assigneeIdToInvalidate = context?.assigneeId ?? ctx.assigneeId;
      // The server is authoritative: reconcile list + balance from the source of truth.
      void qc.invalidateQueries({ queryKey: dayKeyToInvalidate });
      // A completed MISSION (open-ended one-time) leaves the "to do" set or moves to "waiting" — reconcile
      // the Missions lane too (ADR-0141). Harmless when the completed chore was an ordinary dated one.
      void qc.invalidateQueries({ queryKey: missionsKeyToInvalidate });
      void qc.invalidateQueries({ queryKey: balanceKeyToInvalidate });
      // Reconcile the WEEK agenda (the /calendar screen) too — its own query, keyed by weekStart, isn't
      // touched by the day-cache surgery above, so without this it stays stale until a hard reload. The
      // displayed week is unknown here, so invalidate every cached week for this assignee (prefix).
      void qc.invalidateQueries({ queryKey: familyKeys.weeks(assigneeIdToInvalidate) });
      // Also reconcile the single-occurrence detail (chore-detail page): without this it keeps its
      // stale `planned` status, re-arms the complete/photo control, and a second tap fires `complete`
      // on an already-`pending_approval` occurrence → ILLEGAL_OCCURRENCE_TRANSITION (409).
      void qc.invalidateQueries({ queryKey: familyKeys.occurrence(occurrenceId) });
      // An AUTO-APPROVED chore advances the shared cooperative quest bar right away (ADR-0029 — the
      // quest projection consumes `task.occurrence-approved`), so reconcile the family-quest card.
      // Approval-gated chores advance the bar later, when the guardian approves (the review feature
      // invalidates this same key then).
      void qc.invalidateQueries({ queryKey: familyKeys.questsActive() });
      // The hero FELT this effort: refresh the bond ring + narrator voice so they react
      // live on the same screen (ADR-0095 hero overhaul). An auto-approved chore grows bond now; an
      // approval-gated one grows it when the guardian approves (the review feature invalidates then too).
      invalidateHeroSurfaces(qc);
    },
  });

  const complete = useCallback(
    (card: CompletableCard, photoProofKey?: string) => {
      // Clear any prior failure so a retry doesn't keep showing a stale error.
      mutation.reset();
      mutation.mutate({
        // `optimisticId` drives the cache surgery (keyed on the card's `virtual:…`/real id); the wire
        // body addresses the chore by its natural key `(templateId, localDate)` + assignee (ADR-0142).
        optimisticId: card.id,
        templateId: card.templateId,
        localDate: card.scheduledLocalDate,
        assigneeId: card.assigneeId,
        ...(photoProofKey ? { photoProofKey } : {}),
        idempotencyKey: newIdempotencyKey(),
      });
    },
    [mutation],
  );

  const isOccurrencePending = useCallback(
    (occurrenceId: string) => pendingIds.has(occurrenceId),
    [pendingIds],
  );

  return {
    complete,
    isPending: pendingIds.size > 0,
    isOccurrencePending,
    error: mutation.error,
  };
}
